<template>
  <PageShell :title="$t('interface.title.tag', { tag: tagName })">
    <ProductGrid
      :products="filteredProducts"
      :dateRanges="filteredDateRanges"
      :selectedDateRange="selectedDateRange"
      :show-all-time-tab="true"
      :range-counts="rangeCounts"
      :all-time-count="allTimeCount"
      :loading="isLoading"
      @date-range-change="loadDataForSelectedDate"
    />
  </PageShell>
</template>

  <script>
import ProductGrid from './components/ProductGrid.vue';
import PageShell from './components/PageShell.vue';
import { getAllData } from './utils/cacheManager';
import { fetchIndex, fillMissingRanges, resolveInitialRange } from './utils/productData';
import { ALL_TIME_RANGE } from './utils/dateHierarchy';
import { buildRangeStats } from './utils/rangeStats';
import { setMetaTag, setOgLocale, setCanonical, setStructuredData } from './utils/seo';
  
  export default {
    components: {
      ProductGrid,
      PageShell
    },
    data() {
      return {
        tagName: '', // 初始化为空字符串
        products: [], // 所有产品数据
        filteredProductsByDate: {}, // 按日期分组后的过滤数据
        filteredProducts: [], // 当前选中的日期下的产品
        dateRanges: [],
        filteredDateRanges: [], // 过滤后有产品的日期范围
        selectedDateRange: null, // 选中的日期范围
        indexData: null, // 保存 index.json 的数据
        loadedProducts: {}, // 新增：用于缓存已加载的产品数据
        rangeCounts: {}, // 各周 tab 命中数 { "YYYY-MM-DD - YYYY-MM-DD": n }
        allTimeCount: 0, // 全部时间 tab 命中数
        isLoading: false, // 页面级加载态：拉数期间驱动 ProductGrid 骨架屏
      };
    },
    async mounted() {
        // 从路由参数中获取标签名称，并移除可能的语言前缀
        const tagPath = this.$route.params.tag;
        const tagName = decodeURIComponent(tagPath.split('/').pop()); // 获取最后一个部分作为标签名并解码
        this.tagName = tagName;
        await this.loadAllData();
        this.loadDataForSelectedDate(this.selectedDateRange);
        // this.updateMetaInfo();
    },
    watch: {
      '$route.params.tag': function(newTag) {
        // 处理路由参数变化时的标签名称
        const tagName = decodeURIComponent(newTag.split('/').pop());
        this.tagName = tagName;
        this.filterProductsByTagAndDate(tagName, this.selectedDateRange);
        this.updateMetaInfo();
      },
      'selectedDateRange': function() {
        this.updateMetaInfo();
      },
      '$i18n.locale': function() {
        this.updateMetaInfo();
      }
    },
    methods: {
      async loadAllData() {
        // 标签页默认落在「全部时间」，必须先补齐所有周才能算命中，故骨架覆盖整段拉数过程
        this.isLoading = true;
        try {
          const { indexData, dateRanges } = await fetchIndex();
          this.indexData = indexData;
          this.dateRanges = dateRanges;

          // 先批量从 IndexedDB 回填，再补齐缺失区间（非最新才缓存）
          const cachedData = await getAllData(dateRanges);
          this.filteredProductsByDate = cachedData;
          await fillMissingRanges({ dataMap: this.filteredProductsByDate, indexData: this.indexData, dateRanges: this.dateRanges });

          // 处理URL日期参数，默认全部时间（搜索/标签页新需求：默认展示全部时间命中）
          const urlDate = this.$route.params.date ? decodeURIComponent(this.$route.params.date) : null;
          this.selectedDateRange = resolveInitialRange({
            urlDate,
            dateRanges,
            indexData,
            defaultPolicy: 'allTime',
          });
          // 只在没有日期参数时更新URL
          if (!urlDate && this.selectedDateRange) {
            this.$router.replace({
              name: `tagPage-${this.$i18n.locale}`,
              params: {
                tag: this.tagName,
                date: this.selectedDateRange
              },
              replace: true
            }).catch(() => {});
          }

          // 初始化数据过滤
          this.filterProductsByTagAndDate(this.tagName, this.selectedDateRange);
        } catch (error) {
          console.error('Error loading data:', error);
        } finally {
          this.isLoading = false; // 兜底：异常路径同样要撤下骨架
        }
      },
      filterProductsByTagAndDate(tag, dateRange) {
        const lowerCaseTag = tag.toLowerCase(); // 将标签转换为小写
      
        // 检查缓存中是否已有按日期范围筛选的结果（含全部时间）
        if (dateRange && this.loadedProducts[dateRange]) {
            this.filteredProducts = this.loadedProducts[dateRange]; // 从缓存加载筛选结果
          return;
        }
        
        const filteredByTag = {}; // 存储筛选后的产品数据

        // 遍历按日期分组的数据，筛选包含指定标签的产品
        for (let range in this.filteredProductsByDate) {
            const filteredProducts = this.filteredProductsByDate[range].filter(product => {
                // 比较每个产品的标签，确保标签不区分大小写
                return [product.tag1, product.tag2, product.tag3].some(t => t && t.toLowerCase() === lowerCaseTag);
        });

            // 将每个有匹配项的日期范围结果缓存到 loadedProducts 中
            if (filteredProducts.length > 0) {
                filteredByTag[range] = filteredProducts;
                this.loadedProducts[range] = filteredProducts; // 缓存筛选结果
            }
      }
    
        // 跨周合并去重 + 各周命中数，统一收敛到 rangeStats（消除与 SearchPage 的逐行重复）
        const stats = buildRangeStats(filteredByTag, { dedupeKey: p => p.url || p.name });
        this.filteredDateRanges = stats.filteredDateRanges;
        this.rangeCounts = stats.rangeCounts;
        this.allTimeCount = stats.allTimeCount;
        // 缓存全部时间命中，供 tab 切换快速命中（不写入 filteredDateRanges，避免被当作周渲染）
        this.loadedProducts[ALL_TIME_RANGE] = stats.allTimeList;
      
        // 根据所选日期范围更新筛选结果
        if (dateRange === ALL_TIME_RANGE) {
            // 全部时间：直接展示跨周合并的命中（不覆盖 selectedDateRange）
            this.filteredProducts = stats.allTimeList;
        } else if (dateRange && filteredByTag[dateRange]) {
            this.filteredProducts = filteredByTag[dateRange];
        } else if (this.filteredDateRanges.length > 0) {
            // 如果没有特定日期范围，则默认加载最新的日期范围
            this.selectedDateRange = this.filteredDateRanges[this.filteredDateRanges.length - 1];
            this.filteredProducts = filteredByTag[this.selectedDateRange];
        } else {
            // 若无符合条件的产品，则返回空数组
            this.filteredProducts = [];
      }
    },
      loadDataForSelectedDate(dateRange) {
        if (dateRange === this.selectedDateRange) {
          return; // 如果选择的是同一个日期范围，不做任何操作
        }
        
        this.selectedDateRange = dateRange;
        
        // 更新URL，包含标签和日期信息
        this.$router.replace({
          name: `tagPage-${this.$i18n.locale}`,
          params: {
            tag: this.tagName,
            date: this.selectedDateRange
          },
          replace: true
        }).catch(() => {});
        
        // 检查当前日期范围的数据是否已经加载
        if (this.loadedProducts[dateRange]) {
          this.filteredProducts = this.loadedProducts[dateRange];
          return;
        }
        
        // 否则需要过滤数据
        this.filterProductsByTagAndDate(this.tagName, dateRange);
      },
      updateMetaInfo() {
        const title = this.$t('interface.pageTitle.tag', { tag: this.tagName });
        const description = this.$t('meta.tagDescription', { tag: this.tagName });
        const keywords = this.$t('meta.keywords.tag', { tag: this.tagName });
        const currentUrl = window.location.href;
        const imageUrl = 'https://hub.bestreader.ai/assets/aihub.png';
        const locale = this.$i18n.locale;

        // 更新基础 meta
        document.title = title;
        setMetaTag('description', description);
        setMetaTag('keywords', keywords);

        // 更新 Open Graph 标签
        setMetaTag('og:title', title, 'property');
        setMetaTag('og:description', description, 'property');
        setMetaTag('og:url', currentUrl, 'property');
        setMetaTag('og:image', imageUrl, 'property');
        setOgLocale(locale); // 修正：ja/ko 不再被错标为 en_US
        setMetaTag('og:site_name', this.$t('interface.ai_inspiration_hub'), 'property');

        // 更新 Twitter 卡片
        setMetaTag('twitter:title', title);
        setMetaTag('twitter:description', description);
        setMetaTag('twitter:image', imageUrl);

        // 更新作者信息
        setMetaTag('author', this.$t('interface.ai_inspiration_hub'));

        // 更新 canonical 链接，包含标签和日期信息
        const canonicalUrl = `https://hub.bestreader.ai/${locale}/tag/${encodeURIComponent(this.tagName)}${this.selectedDateRange ? '/' + encodeURIComponent(this.selectedDateRange) : ''}`;
        setCanonical(canonicalUrl);

        // 更新结构化数据
        setStructuredData({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: title,
          description: description,
          url: currentUrl,
          inLanguage: locale,
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: this.filteredProducts.map((product, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Product',
                name: product.name,
                description: product.description,
                url: product.url,
                image: product.logo,
              },
            })),
          },
        });
      },
    },
  };
  </script>
  
  <style scoped>
  /* 标题区样式已抽到 src/components/siteTitle.vue（全局页面共享） */
  </style>